import { Stack, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AndieIconAnimated from './AndieIconAnimated';
import { TFunction } from 'i18next';

const INTERVAL_MS = 6000;

// Tolgee injects invisible Unicode format chars (ZWJ/ZWNJ) into every translated string for
// in-context editing. Strip them before checking whether a key actually resolved to real text.
const TOLGEE_MARKERS = /\p{Cf}/gu;

function hasText(v: string): boolean {
    return v.replace(TOLGEE_MARKERS, '').trim().length > 0;
}

function resolveMessages(t: TFunction, base: string): string[] {
    const raw: string[] = [];
    for (let i = 0; i < 20; i++) {
        const v = t(`${base}.${i}`, { defaultValue: '' });
        if (!hasText(v)) break;
        raw.push(v);
    }
    if (raw.length === 0) return [];
    // Loop back to first message so the cycle never has a dead-end
    return _.shuffle(raw);
}

const AndieThinkingIndicator = memo(
    ({ textWriting, toolName }: { textWriting?: boolean; toolName?: string }) => {
        const { t } = useTranslation();

        const writingMsgs = useMemo(() => resolveMessages(t, 'andie.thinkingMessages.textWriting'), [t]);

        const messages = useMemo<string[]>(() => {
            if (textWriting && writingMsgs.length > 0) return writingMsgs;
            if (toolName) {
                const toolMsgs = resolveMessages(t, `andie.thinkingMessages.tool.${toolName}`);
                if (toolMsgs.length > 0) return toolMsgs;
            }
            return resolveMessages(t, 'andie.thinkingMessages.generic');
        }, [t, toolName, textWriting, writingMsgs]);

        const [currentIdx, setCurrentIdx] = useState(0);
        const intervalRef = useRef<ReturnType<typeof setInterval>>();

        useEffect(() => {
            setCurrentIdx(0);
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(
                () => setCurrentIdx((p) => (p + 1) % messages.length),
                INTERVAL_MS,
            );
            return () => clearInterval(intervalRef.current);
        }, [messages]);

        return (
            <Stack direction="row" alignItems="center" gap={1.5} py={2}>
                <AndieIconAnimated size={32} />
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIdx}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontsize: 13, lineHeight: 1.2 }}
                        >
                            {messages[currentIdx]}
                        </Typography>
                    </motion.div>
                </AnimatePresence>
            </Stack>
        );
    },
);

AndieThinkingIndicator.displayName = 'AndieThinkingIndicator';
export default AndieThinkingIndicator;
